import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'kovar_cart_v1';

const isSameLine = (a, b) => {
  if (a.product_slug !== b.product_slug) return false;
  const slugsA = (a.addons || []).map(x => x.slug).sort().join('|');
  const slugsB = (b.addons || []).map(x => x.slug).sort().join('|');
  return slugsA === slugsB;
};

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadInitial());

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (err) { /* quota */ }
  }, [items]);

  const addItem = useCallback((newItem) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => isSameLine(x, newItem));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (newItem.quantity || 1) };
        return next;
      }
      return [...prev, { ...newItem, quantity: newItem.quantity || 1 }];
    });
  }, []);

  const removeAt = useCallback((idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const setQuantityAt = useCallback((idx, qty) => {
    setItems((prev) => prev.map((item, i) => i === idx ? { ...item, quantity: Math.max(1, qty | 0) } : item));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const itemCount = useMemo(() => items.reduce((n, it) => n + (it.quantity || 1), 0), [items]);
  const totalUSD = useMemo(() => items.reduce((sum, it) => {
    const addonSum = (it.addons || []).reduce((s, a) => s + Number(a.price || 0), 0);
    return sum + (Number(it.base_price || 0) + addonSum) * (it.quantity || 1);
  }, 0), [items]);

  const value = useMemo(() => ({
    items, addItem, removeAt, setQuantityAt, clear, itemCount, totalUSD,
  }), [items, addItem, removeAt, setQuantityAt, clear, itemCount, totalUSD]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
